import { useCallback } from "react";
import { appendRichText, hasRichTextContent, richTextToPlainText } from "../utils/richTextHelpers";

export default function useAiWorkflowOperations({
  fields,
  setFields,
  beurteilungGeneratePrompt,
  defaultBeurteilungGeneratePrompt,
  systemPrompt,
  buildCurrentRadiologyContext,
  buildBeurteilungStyleInstruction,
  normalizeBeurteilungStyleProfile,
  beurteilungStyleProfile,
  aiUserInstructionLabel,
  requestAiText,
  compressBeurteilungIfNeeded,
  setIsGenerating,
  showStatus,
  showApiError,
  aiBefundPrompt,
  aiBeurteilungPrompt,
  aiBefundResponse,
  aiBefundSelection,
  setIsAiBefundLoading,
  setIsAiBeurteilungLoading,
  setAiBefundResponse,
  setAiBefundPrompt,
  buildAiAssistantContext,
  aiBefundAssistantSystemPrompt,
  extractBefundOnly,
  aiMsgEnterBefundFirst,
  aiMsgEnterPromptFirst,
  aiMsgSelectBefundFirst,
  aiMsgNoResponse,
  aiMsgEnterBeurteilungFirst,
  applyBefundWithHistory,
  applyBeurteilungWithHistory,
  deriveChangedRange,
  createAiBefundHistoryEntry,
  setAiBefundHistory,
  aiBefundHistoryLimit,
  setAiBefundLastAction,
  aiBefundHistory,
  aiMsgHistoryEntryLoaded,
  aiMsgHistoryEntryRemoved,
  aiMsgHistoryCleared,
  createTimeBasedId,
  aiQuickPrompts,
  setAiQuickPrompts,
  aiBeurteilungQuickPrompts,
  setAiBeurteilungQuickPrompts,
  aiQuickPromptsLimit,
  saveUserPrivateDataDetailed,
  setAiPromptSyncStatus,
  formatSupabaseError,
  userDataKeyAiQuickPrompts,
  userDataKeyAiBeurteilungQuickPrompts,
  aiMsgPromptSaved,
  aiMsgPromptDeleted,
  aiMsgBeurteilungPromptSaved,
  aiMsgBeurteilungPromptDeleted,
  requireAuth,
  persistBeurteilungGeneratePrompt,
}) {
  const normalizeAiPromptEntry = useCallback(({ id, title, text, target, idPrefix }) => ({
    id: id || `${idPrefix}-${createTimeBasedId()}`,
    title: (title || "").trim() || "Ohne Titel",
    text: text.trim(),
    target: target === "assistant" ? "assistant" : "report",
  }), [createTimeBasedId]);

  const upsertAiPromptList = useCallback((promptList, normalizedPrompt) => {
    const existingIndex = promptList.findIndex((entry) => entry.id === normalizedPrompt.id);
    if (existingIndex === -1) {
      return [normalizedPrompt, ...promptList].slice(0, aiQuickPromptsLimit);
    }

    return promptList.map((entry, entryIndex) => (
      entryIndex === existingIndex ? normalizedPrompt : entry
    ));
  }, [aiQuickPromptsLimit]);

  const persistAiPromptList = useCallback(async ({
    dataKey,
    promptList,
    setPromptList,
    successMessage,
    saveErrorLabel,
  }) => {
    const persisted = await saveUserPrivateDataDetailed(dataKey, promptList);
    if (!persisted.ok) {
      setAiPromptSyncStatus("error");
      showStatus(`${saveErrorLabel}: ${formatSupabaseError(persisted.error)}`, true);
      return false;
    }

    setPromptList(promptList);
    setAiPromptSyncStatus("cloud");
    showStatus(successMessage);
    return true;
  }, [formatSupabaseError, saveUserPrivateDataDetailed, setAiPromptSyncStatus, showStatus]);

  const deleteAiPromptFromList = useCallback(async ({
    dataKey,
    currentPromptList,
    promptId,
    setPromptList,
    successMessage,
    deleteErrorLabel,
  }) => {
    const nextPromptList = currentPromptList.filter((entry) => entry.id !== promptId);
    const persisted = await saveUserPrivateDataDetailed(dataKey, nextPromptList);
    if (!persisted.ok) {
      setAiPromptSyncStatus("error");
      showStatus(`${deleteErrorLabel}: ${formatSupabaseError(persisted.error)}`, true);
      return false;
    }

    setPromptList(nextPromptList);
    setAiPromptSyncStatus("cloud");
    showStatus(successMessage);
    return true;
  }, [formatSupabaseError, saveUserPrivateDataDetailed, setAiPromptSyncStatus, showStatus]);

  const generateBeurteilung = useCallback(async () => {
    if (!hasRichTextContent(fields.befund)) { showStatus("Befund darf nicht leer sein.", true); return; }
    const effectivePrompt = (beurteilungGeneratePrompt || "").trim() || defaultBeurteilungGeneratePrompt;
    setIsGenerating(true);
    setFields((f) => ({ ...f, beurteilung: "" }));
    try {
      const result = await requestAiText({
        systemPrompt,
        context: [
          buildCurrentRadiologyContext(),
          `\nStilprofil für Beurteilung: ${buildBeurteilungStyleInstruction(normalizeBeurteilungStyleProfile(beurteilungStyleProfile))}`,
          `\n${aiUserInstructionLabel}`,
          effectivePrompt,
        ].join("\n"),
        maxTokens: 1000,
      });
      const finalResult = await compressBeurteilungIfNeeded(result);
      if (finalResult) {
        setFields((f) => ({ ...f, beurteilung: finalResult }));
        showStatus("Beurteilung erstellt ✓");
      } else {
        showStatus("Fehler: Keine Antwort erhalten.", true);
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setIsGenerating(false);
    }
  }, [aiUserInstructionLabel, beurteilungGeneratePrompt, beurteilungStyleProfile, buildBeurteilungStyleInstruction, buildCurrentRadiologyContext, compressBeurteilungIfNeeded, defaultBeurteilungGeneratePrompt, fields.befund, normalizeBeurteilungStyleProfile, requestAiText, setFields, setIsGenerating, showApiError, showStatus, systemPrompt]);

  const runAiBefundAssistant = useCallback(async (mode = "full", promptOverride = "") => {
    const effectivePrompt = (promptOverride || aiBefundPrompt || "").trim();

    if (!hasRichTextContent(fields.befund)) {
      showStatus(aiMsgEnterBefundFirst, true);
      return;
    }
    if (!effectivePrompt) {
      showStatus(aiMsgEnterPromptFirst, true);
      return;
    }
    if (mode === "selection" && !aiBefundSelection.text.trim()) {
      showStatus(aiMsgSelectBefundFirst, true);
      return;
    }

    setIsAiBefundLoading(true);
    setAiBefundResponse("");
    try {
      const context = buildAiAssistantContext({
        mode,
        promptText: effectivePrompt,
      });
      const result = await requestAiText({
        systemPrompt: aiBefundAssistantSystemPrompt,
        context,
        maxTokens: 1600,
      });
      const befundOnlyResult = extractBefundOnly(result);
      if (!befundOnlyResult) {
        showStatus(aiMsgNoResponse, true);
        return;
      }
      const currentPrompt = effectivePrompt;
      let nextBefund = befundOnlyResult;
      const plainBefund = richTextToPlainText(fields.befund);
      let highlightRange = deriveChangedRange(plainBefund, befundOnlyResult);

      if (mode === "selection") {
        const selectedText = String(aiBefundSelection.text || "");
        const selectionStart = plainBefund.indexOf(selectedText);
        nextBefund = selectionStart === -1
          ? plainBefund
          : `${plainBefund.slice(0, selectionStart)}${befundOnlyResult}${plainBefund.slice(selectionStart + selectedText.length)}`;
        highlightRange = {
          start: Math.max(0, selectionStart),
          end: Math.max(0, selectionStart) + befundOnlyResult.length,
        };
      }

      const applied = applyBefundWithHistory(
        nextBefund,
        highlightRange,
        mode === "selection"
          ? "AI-Anpassung direkt in die Auswahl übernommen ✓"
          : "AI-Anpassung direkt im Befund übernommen ✓"
      );
      if (!applied) {
        return;
      }

      setAiBefundResponse(befundOnlyResult);
      setAiBefundHistory((prev) => [
        createAiBefundHistoryEntry({
          prompt: currentPrompt,
          response: befundOnlyResult,
          mode,
        }),
        ...prev,
      ].slice(0, aiBefundHistoryLimit));
      setAiBefundLastAction({
        prompt: currentPrompt,
        mode,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      showApiError(err);
    } finally {
      setIsAiBefundLoading(false);
    }
  }, [aiBefundAssistantSystemPrompt, aiBefundHistoryLimit, aiBefundPrompt, aiBefundSelection, aiMsgEnterBefundFirst, aiMsgEnterPromptFirst, aiMsgNoResponse, aiMsgSelectBefundFirst, applyBefundWithHistory, buildAiAssistantContext, createAiBefundHistoryEntry, deriveChangedRange, extractBefundOnly, fields.befund, requestAiText, setAiBefundHistory, setAiBefundLastAction, setAiBefundResponse, setIsAiBefundLoading, showApiError, showStatus]);

  const runAiBeurteilungAssistant = useCallback(async (promptOverride = "") => {
    const effectivePrompt = (promptOverride || aiBeurteilungPrompt || "").trim();

    if (!hasRichTextContent(fields.beurteilung)) {
      showStatus(aiMsgEnterBeurteilungFirst, true);
      return;
    }
    if (!effectivePrompt) {
      showStatus(aiMsgEnterPromptFirst, true);
      return;
    }

    setIsAiBeurteilungLoading(true);
    try {
      const context = [
        buildCurrentRadiologyContext(),
        `\nStilprofil für Beurteilung: ${buildBeurteilungStyleInstruction(normalizeBeurteilungStyleProfile(beurteilungStyleProfile))}`,
        "\nAktuelle Beurteilung:",
        richTextToPlainText(fields.beurteilung),
        `\n${aiUserInstructionLabel}`,
        effectivePrompt,
      ].join("\n");

      const result = await requestAiText({
        systemPrompt,
        context,
        maxTokens: 1000,
      });

      const finalResult = await compressBeurteilungIfNeeded(result);

      if (!finalResult) {
        showStatus(aiMsgNoResponse, true);
        return;
      }

      applyBeurteilungWithHistory(finalResult, "AI-Anpassung direkt in die Beurteilung übernommen ✓");
    } catch (err) {
      showApiError(err);
    } finally {
      setIsAiBeurteilungLoading(false);
    }
  }, [aiBeurteilungPrompt, aiMsgEnterBeurteilungFirst, aiMsgEnterPromptFirst, aiMsgNoResponse, aiUserInstructionLabel, applyBeurteilungWithHistory, beurteilungStyleProfile, buildBeurteilungStyleInstruction, buildCurrentRadiologyContext, compressBeurteilungIfNeeded, fields.beurteilung, normalizeBeurteilungStyleProfile, requestAiText, setIsAiBeurteilungLoading, showApiError, showStatus, systemPrompt]);

  const restoreAiBefundHistoryEntry = useCallback((entryId) => {
    const entry = aiBefundHistory.find((item) => item.id === entryId);
    if (!entry) return;
    setAiBefundPrompt(entry.prompt || "");
    setAiBefundResponse(entry.response || "");
    showStatus(aiMsgHistoryEntryLoaded);
  }, [aiBefundHistory, aiMsgHistoryEntryLoaded, setAiBefundPrompt, setAiBefundResponse, showStatus]);

  const deleteAiBefundHistoryEntry = useCallback((entryId) => {
    setAiBefundHistory((prev) => prev.filter((item) => item.id !== entryId));
    showStatus(aiMsgHistoryEntryRemoved);
  }, [aiMsgHistoryEntryRemoved, setAiBefundHistory, showStatus]);

  const clearAiBefundHistory = useCallback(() => {
    setAiBefundHistory([]);
    showStatus(aiMsgHistoryCleared);
  }, [aiMsgHistoryCleared, setAiBefundHistory, showStatus]);

  const saveAiQuickPrompt = useCallback(async ({ id, title, text, target }) => {
    if (!requireAuth()) return false;
    if (!text?.trim()) {
      showStatus("Prompt-Text darf nicht leer sein.", true);
      return false;
    }

    const normalizedPrompt = normalizeAiPromptEntry({
      id,
      title,
      text,
      target,
      idPrefix: "quick-custom",
    });
    const nextPromptList = upsertAiPromptList(aiQuickPrompts, normalizedPrompt);

    return persistAiPromptList({
      dataKey: userDataKeyAiQuickPrompts,
      promptList: nextPromptList,
      setPromptList: setAiQuickPrompts,
      successMessage: aiMsgPromptSaved,
      saveErrorLabel: "AI-Prompt konnte nicht in Supabase gespeichert werden",
    });
  }, [aiMsgPromptSaved, aiQuickPrompts, normalizeAiPromptEntry, persistAiPromptList, requireAuth, setAiQuickPrompts, showStatus, upsertAiPromptList, userDataKeyAiQuickPrompts]);

  const deleteAiQuickPrompt = useCallback(async (id) => {
    if (!requireAuth()) return;
    await deleteAiPromptFromList({
      dataKey: userDataKeyAiQuickPrompts,
      currentPromptList: aiQuickPrompts,
      promptId: id,
      setPromptList: setAiQuickPrompts,
      successMessage: aiMsgPromptDeleted,
      deleteErrorLabel: "AI-Prompt konnte nicht in Supabase gelöscht werden",
    });
  }, [aiMsgPromptDeleted, aiQuickPrompts, deleteAiPromptFromList, requireAuth, setAiQuickPrompts, userDataKeyAiQuickPrompts]);

  const saveAiBeurteilungQuickPrompt = useCallback(async ({ id, title, text, target }) => {
    if (!requireAuth()) return false;
    if (!text?.trim()) {
      showStatus("Prompt-Text darf nicht leer sein.", true);
      return false;
    }

    const normalizedPrompt = normalizeAiPromptEntry({
      id,
      title,
      text,
      target,
      idPrefix: "beurteilung-quick-custom",
    });
    const nextPromptList = upsertAiPromptList(aiBeurteilungQuickPrompts, normalizedPrompt);

    return persistAiPromptList({
      dataKey: userDataKeyAiBeurteilungQuickPrompts,
      promptList: nextPromptList,
      setPromptList: setAiBeurteilungQuickPrompts,
      successMessage: aiMsgBeurteilungPromptSaved,
      saveErrorLabel: "AI-Beurteilung-Prompt konnte nicht in Supabase gespeichert werden",
    });
  }, [aiBeurteilungQuickPrompts, aiMsgBeurteilungPromptSaved, normalizeAiPromptEntry, persistAiPromptList, requireAuth, setAiBeurteilungQuickPrompts, showStatus, upsertAiPromptList, userDataKeyAiBeurteilungQuickPrompts]);

  const deleteAiBeurteilungQuickPrompt = useCallback(async (id) => {
    if (!requireAuth()) return;
    await deleteAiPromptFromList({
      dataKey: userDataKeyAiBeurteilungQuickPrompts,
      currentPromptList: aiBeurteilungQuickPrompts,
      promptId: id,
      setPromptList: setAiBeurteilungQuickPrompts,
      successMessage: aiMsgBeurteilungPromptDeleted,
      deleteErrorLabel: "AI-Beurteilung-Prompt konnte nicht in Supabase gelöscht werden",
    });
  }, [aiBeurteilungQuickPrompts, aiMsgBeurteilungPromptDeleted, deleteAiPromptFromList, requireAuth, setAiBeurteilungQuickPrompts, userDataKeyAiBeurteilungQuickPrompts]);

  const saveBeurteilungGeneratePromptHandler = useCallback(async () => {
    if (!requireAuth()) return;
    const persisted = await persistBeurteilungGeneratePrompt(beurteilungGeneratePrompt);
    if (!persisted) {
      showStatus("Prompt konnte nicht gespeichert werden.", true);
      return;
    }
    showStatus("Prompt gespeichert ✓");
  }, [beurteilungGeneratePrompt, persistBeurteilungGeneratePrompt, requireAuth, showStatus]);

  const applyAiBefundResponse = useCallback((mode = "replace") => {
    const response = aiBefundResponse.trim();
    if (!response) return;

    if (mode === "selection") {
      if (aiBefundSelection.start == null || aiBefundSelection.end == null || aiBefundSelection.start === aiBefundSelection.end) {
        showStatus("Bitte zuerst einen Abschnitt im Befund markieren.", true);
        return;
      }

      setFields((prev) => {
        const plainBefund = richTextToPlainText(prev.befund);
        const selectedText = String(aiBefundSelection.text || "");
        const selectionStart = plainBefund.indexOf(selectedText);
        return {
          ...prev,
          befund: selectionStart === -1
            ? prev.befund
            : `${plainBefund.slice(0, selectionStart)}${response}${plainBefund.slice(selectionStart + selectedText.length)}`,
        };
      });
      showStatus("Markierter Befundabschnitt durch AI-Vorschlag ersetzt ✓");
      return;
    }

    setFields((prev) => ({
      ...prev,
      befund: mode === "append" ? appendRichText(prev.befund, response) : response,
    }));

    showStatus(mode === "append" ? "AI-Vorschlag an Befund angehängt ✓" : "Befund durch AI-Vorschlag ersetzt ✓");
  }, [aiBefundResponse, aiBefundSelection, setFields, showStatus]);

  return {
    generateBeurteilung,
    runAiBefundAssistant,
    runAiBeurteilungAssistant,
    restoreAiBefundHistoryEntry,
    deleteAiBefundHistoryEntry,
    clearAiBefundHistory,
    saveAiQuickPrompt,
    deleteAiQuickPrompt,
    saveAiBeurteilungQuickPrompt,
    deleteAiBeurteilungQuickPrompt,
    saveBeurteilungGeneratePromptHandler,
    applyAiBefundResponse,
  };
}
